<template>
  <div class="companies-root">
  <div class="companies-page">
    <div class="companies-container">
      <div class="companies-layout container">
        <div class="companies-header d-flex flex-column align-items-center">
          <h5 class="font-16 mb-2 font-bold">キャリア登録を希望する企業を選択してください</h5>

          <div class="d-flex justify-content-center align-items-center mb-2">
            <input
              class="custom-checkbox me-3"
              type="checkbox"
              v-model="selectAllCompaniesModel"
              @change="handleSelectAllChange"
              id="select_all"
              name="select_all"
            />
            <label
              class="form-check-label font-16 mb-0"
              for="select_all"
              style="cursor: pointer;"
            >
              すべて選択（全{{ companies.length }}社）
            </label>
          </div>
        </div>

        <div class="companies-scroll">
          <div class="companies-table">
            <div class="companies-grid">
              <div
                v-for="company in companies"
                :key="company._id"
                class="company-card"
                :class="{ 'selected': isSelected(company._id) }"
                @click="toggleCompany(company._id)"
              >
                <div class="company-content">
                  <span class="company-name">{{ company.name }}</span>
                  <span v-if="isSelected(company._id)" class="selected-badge desktop-badge">選択済</span>
                </div>
                <span v-if="isSelected(company._id)" class="selected-badge mobile-badge">選択済</span>
              </div>
            </div>
          </div>
        </div>

        <div class="buttons-container">
          <button
            @click="handleExitWithoutSelection"
            class="btn-exit"
            :class="{ 'unclickable': other_companies.length > 0 }"
          >
            選択せず終了する
          </button>
          
          <button
            class="btn-register"
            :class="{ 'unclickable': other_companies.length === 0 }"
            @click="confirm"
          >
            登録確認に進む
          </button>
        </div>
      </div>
    </div>
  </div>

    <!-- Confirmation Modal -->
    <b-modal
      ref="confirmModal"
      hide-header
      hide-footer
      centered
      size="lg"
      @hidden="hideModal"
    >
      <div class="modal-title">
        以下企業へキャリア登録しますか？
      </div>
      
      <!-- Selected Companies List (moved up) -->
      <div class="modal-companies" :class="{ 'scrollable': selectedCompanyName.length > 10 }">
        <div class="companies-list">
          <span
            class="company-item"
            v-for="name in selectedCompanyName"
            :key="name"
          >
            {{ name }}
          </span>
        </div>
      </div>

      <!-- Notice Box (moved down) -->
      <div class="modal-notice">
        <p class="notice-header">
          直近在籍企業など、あなたの情報を公開したくない企業が含まれていないか十分注意してください。
        </p>
        <ul class="notice-list">
          <li><strong>あなたのキャリア登録情報が企業へ公開されます。</strong></li>
          <li><strong>企業側はあなたが他にキャリア登録している企業を知ることはできません。</strong></li>
          <li><strong>許諾無しにあなたがキャリア登録していない企業へ情報が公開されることはありません。</strong></li>
        </ul>
      </div>

      <div class="modal-footer-desktop">
        <button class="btn-cancel" @click="shareCompanyResponse(false)">
          企業選択に戻る
        </button>
        <button class="btn-confirm" @click="shareCompanyResponse(true)">
          注意事項に同意して<br />
          <span class="font-bold">登録する</span>
        </button>
      </div>
      <div class="modal-footer-mobile">
        <button class="btn-confirm" @click="shareCompanyResponse(true)">
          注意事項に同意して<br />
          <span class="font-bold">登録する</span>
        </button>
        <button class="btn-cancel" @click="shareCompanyResponse(false)">
          企業選択に戻る
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Api from '@/config/api';
import swal from 'sweetalert';

export default {
  name: 'CompaniesList',
  props: {
    companies: {
      type: Array,
      required: true
    },
    companyData: {
      type: Object,
      default: null
    },
    token: {
      type: String,
      required: true
    },
    userId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectAllCompaniesModel: false,
      other_companies: [],
      selectedCompanyName: [],
      isManualSelectAllChange: true, // Track if select all change is manual
    };
  },
  computed: {
    o_companies() {
      return this.other_companies;
    }
  },
  watch: {
    selectAllCompaniesModel(newValue, oldValue) {
      // Only trigger if user manually clicked the "select all" checkbox
      // Don't trigger when it's updated programmatically from toggleCompany
      if (this.isManualSelectAllChange) {
        if (newValue) {
          this.selectAllCompanies();
        } else {
          this.unselectAllcompanies();
        }
      }
      this.isManualSelectAllChange = true; // Reset for next change
    },
  },
  methods: {
    handleSelectAllChange() {
      // Mark this as a manual change
      this.isManualSelectAllChange = true;
      // The watcher will handle the actual selection/deselection
    },
    isSelected(companyId) {
      return this.other_companies.includes(companyId);
    },
    toggleCompany(companyId) {
      const index = this.other_companies.indexOf(companyId);
      if (index > -1) {
        this.other_companies.splice(index, 1);
      } else {
        this.other_companies.push(companyId);
      }
      
      // Update selectAll checkbox without triggering the watcher action
      this.isManualSelectAllChange = false;
      this.selectAllCompaniesModel = this.other_companies.length === this.companies.length;
    },
    shareCompanyResponse(result) {
      if (result) {
        this.afterVerify();
      } else {
        this.$refs.confirmModal.hide();
      }
    },
    handleExitWithoutSelection() {
      // Only allow exit without selection if no companies are selected
      if (this.other_companies.length > 0) {
        // Don't proceed if companies are selected
        return;
      }
      this.$emit('registration-complete');
      this.$router.push('/company-registration-complete');
    },
    hideModal() {
      this.selectedCompanyName = [];
    },
    async exitWithoutSelection() {
      await swal({
        title: '企業が選択されていません。',
        text: '',
        icon: 'warning',
        button: {
          text: '戻る',
          value: false,
          visible: true,
          closeModal: true,
        },
      });
    },
    async confirm() {
      window.scrollTo(0, 0);
      if (this.other_companies.length > 0) {
        this.other_companies.forEach((comp, index) => {
          const resultedCompany = this.companies.find(com => com._id === comp);
          this.selectedCompanyName.push(`${index + 1}. ${resultedCompany.name}`);
        });
        this.$refs.confirmModal.show();
      } else {
        await this.exitWithoutSelection();
      }
    },
    async afterVerify() {
      try {
        await this.axios({
          method: 'post',
          data: {
            token: this.token,
            id: this.userId,
            other_companies: this.o_companies,
            first_time_registration: true,
          },
          url: Api.url(`${Api.endpoints.afterVerifyEmail}?selectAll=${this.selectAllCompaniesModel}`),
        });
        this.$refs.confirmModal.hide();
        this.$emit('registration-complete');
        this.$router.push('/company-registration-complete');
      } catch (error) {
        if (error.response && error.response.status === 400) {
          await swal({
            text: 'このリンクは期限切れか無効になっています。',
            icon: 'warning',
            buttons: {
              ok: 'はい',
            },
          });
          this.$emit('redirect', 'login');
        } else if (error.response && error.response.status === 503) {
          this.$emit('redirect', 'logout');
        }
      }
    },
    selectAllCompanies() {
      this.other_companies = this.companies.map(company => company._id);
    },
    unselectAllcompanies() {
      this.other_companies = [];
    },
  },
};
</script>

<style scoped>
.selected-badge.desktop-badge {
  display: inline-block;
}
.selected-badge.mobile-badge {
  display: none;
}
.companies-root {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.companies-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.companies-container {
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.companies-layout {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 15px;
  padding-right: 15px;
  padding-bottom: 0;
  overflow: hidden;
}

.companies-header {
  flex: 0 0 auto;
}

.companies-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.buttons-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 auto;
  gap: 30px;
  padding: 16px 0 calc(24px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  z-index: 10;
  width: 100%;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
}

.content-wrapper {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.font-18 {
  font-size: 18px;
}

.font-16 {
  font-size: 16px;
}

.font-bold {
  font-weight: bold;
}

.custom-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.companies-table {
  width: 100%;
  max-width: 1100px;
  background: #fff;
  padding: 20px;
  margin: 0 auto;
}

.companies-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  width: 100%;
}

.company-card {
  border: 1px solid #ddd;
  padding: 15px 20px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 60px;
  display: flex;
  align-items: center;
  position: relative;
}

.company-card:hover {
  background: #f5f5f5;
  z-index: 1;
}

.company-card.selected {
  background: #d4f1f4;
  border-color: #076d6c;
  z-index: 2;
}

.company-content {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 10px;
  /* position: relative; */
}

.company-name {
  font-size: 14px;
  font-weight: 600;
  color: #010001;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.4;
}

.selected-badge {
  font-size: 14px;
  color: #009c9f;
  font-weight: bold;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-exit {
  background: #f1f4f8;
  border: 1px solid #f1f4f8;
  color: #505c6a;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  padding: 10px 50px;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.btn-exit:hover:not(.unclickable) {
  background: #e0e4e8;
}

.btn-exit.unclickable {
  cursor: not-allowed;
  opacity: 0.5;
}

.btn-exit.unclickable:hover {
  background: #f1f4f8;
}

.btn-register {
  background: #ff0000;
  border: 1px solid #ff0000;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  padding: 10px 50px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-register:hover:not(.unclickable) {
  background: #c20101;
}

.btn-register.unclickable {
  cursor: not-allowed;
  opacity: 0.5;
}

.btn-register.unclickable:hover {
  background: #ff0000;
}

/* Modal Styles */
::v-deep .modal-body {
  padding: 0 !important;
}

.modal-title {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  padding: 30px 20px 20px;
  color: #000;
}

.modal-notice {
  background: #dee9f5;
  padding: 20px;
  margin: 0 auto 20px;
  border-radius: 8px;
  max-width: 700px;
}

.notice-header {
  color: #ff0000;
  font-weight: bold;
  font-size: 18px;
  text-align: center;
  margin-bottom: 15px;
}

.notice-list {
  list-style-position: outside;
  color: #3c3c3c;
  font-size: 14px;
  line-height: 1.8;
  padding-left: 20px;
  margin-left: 0;
}

.notice-list li {
  margin-bottom: 5px;
  padding-left: 1px;
}

.modal-companies {
  padding: 20px 40px;
  margin: 0 auto 20px;
  max-width: 700px;
  max-height: none;
  overflow-y: visible;
}

.modal-companies.scrollable {
  max-height: 300px;
  overflow-y: auto;
}

.modal-companies::-webkit-scrollbar {
  width: 8px;
}

.modal-companies::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-companies::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.modal-companies::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.companies-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.company-item {
  font-size: 16px;
  color: #333;
}

.modal-footer-desktop {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 20px;
}

.modal-footer-mobile {
  display: none;
}

.btn-cancel {
  background: #666666;
  border: 1px solid #666666;
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  padding: 10px 30px;
  border-radius: 4px;
  cursor: pointer;
  min-width: 200px;
}

.btn-cancel:hover {
  background: #494949;
}

.btn-confirm {
  background: #ff0000;
  border: 1px solid #ff0000;
  color: #fff;
  font-size: 14px;
  padding: 10px 30px;
  border-radius: 4px;
  cursor: pointer;
  min-width: 200px;
  line-height: 1.5;
}

.btn-confirm:hover {
  background: #c20101;
}

@media (min-width: 769px) {
  .companies-header {
    padding-top: 1em;
  }

  .buttons-container {
    box-shadow: none;
  }
}

@media (max-width: 768px) {
  .selected-badge.desktop-badge {
    display: none;
  }
  .selected-badge.mobile-badge {
    display: inline-block;
    position: absolute !important;
    top: 5px;
    right: 8px !important;
    font-size: 11px;
    background: transparent;
    padding: 0;
    margin: 0 !important;
    display: block !important;
    z-index: 10;
  }
  .companies-layout {
    padding-left: 5px;
    padding-right: 5px;
  }

  .companies-table {
    margin: 0;
    padding: 8px;
    width: 100%;
    box-sizing: border-box;
  }

  .companies-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    width: 100%;
  }

  .company-card {
    position: relative;
    justify-content: flex-start;
    align-items: center;
    min-height: 70px;
    padding: 15px 10px;
    font-size: 12px;
    overflow: hidden;
  }

  .company-content {
    width: 100%;
    position: relative;
    display: block !important;
  }

  .company-name {
    text-align: center;
    font-size: 12px;
    padding-right: 0;
    line-height: 1.3;
    word-break: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    width: 100%;
    display: block;
    margin: 0;
    padding-top: 5px;
  }

  .buttons-container {
    flex-direction: row;
    gap: 10px;
    padding: 12px 15px calc(20px + env(safe-area-inset-bottom, 0px));
    flex-wrap: nowrap;
  }

  .btn-register,
  .btn-exit {
    width: auto;
    flex: 1;
    max-width: none;
    padding: 12px 8px;
    font-size: 12px;
    white-space: nowrap;
    min-width: 0;
  }

  .modal-footer-desktop {
    display: none;
  }

  .modal-footer-mobile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    padding: 20px;
    margin-bottom: 15px;
  }

  .btn-cancel,
  .btn-confirm {
    width: 100%;
    max-width: 300px;
  }

  .notice-header {
    font-size: 24px;
    line-height: 1.4;
    text-align: left;
  }

  .notice-list {
    font-size: 14px;
    line-height: 1.2;
  }

  .modal-companies {
    padding: 20px;
    max-height: none;
    overflow-y: visible;
  }
  
  .modal-companies.scrollable {
    max-height: 250px;
    overflow-y: auto;
  }

  .company-item {
    font-size: 14px;
  }
  
  .modal-notice {
    order: -1;
    margin: 0 0 20px 0;
    max-width: none;
    border-radius: 0;
    padding: 10px !important;
  }
  
  .modal-title {
    order: 1;
  }
  
  .modal-companies {
    order: 2;
    text-align: left;
    padding: 30px;
    margin: 0;
  }
  
  .companies-list {
    text-align: left;
    align-items: flex-start;
  }
  
  .company-item {
    text-align: left !important;
    display: block;
    width: 100%;
  }
  
  .modal-footer-mobile {
    order: 3;
  }
  
  ::v-deep .modal-content {
    display: flex;
    flex-direction: column;
  }
  
  ::v-deep .modal-body {
    display: flex;
    flex-direction: column;
  }
}

/* medium phones */
@media (max-width: 404px) {
  .notice-header {
    font-size: 20px;
  }
}

/* small phones */
@media (max-width: 375px) {
  .notice-header {
    font-size: 20px;
  }
}

/* very small devices */
@media (max-width: 360px) {
  .notice-header {
    font-size: 19px;
  }
}
</style>
